export const deviceCategories = [
  'smartphone',
  'tablet',
  'computer',
  'digital_recorder',
  'camera',
  'other',
  'unknown',
] as const;

export const microphoneTypes = [
  'built_in',
  'wired_lavalier',
  'wireless_lavalier',
  'headset',
  'external_handheld',
  'external_directional',
  'other_external',
  'unknown',
] as const;

export type RecordingDeviceCategory = (typeof deviceCategories)[number];
export type RecordingMicrophoneType = (typeof microphoneTypes)[number];
export type CompleteRecordingDeviceSelection = {
  recordingDeviceCategory: RecordingDeviceCategory;
  recordingMicrophoneType: RecordingMicrophoneType;
  recordingDeviceDetails: string;
};

export type RecordingDeviceSelection = {
  recordingDeviceCategory: RecordingDeviceCategory | '';
  recordingMicrophoneType: RecordingMicrophoneType | '';
  recordingDeviceDetails: string;
};

export function recordingDeviceCategoryFromValue(value: string): RecordingDeviceCategory | '' {
  return deviceCategories.find((category) => category === value) ?? '';
}

export function recordingMicrophoneTypeFromValue(value: string): RecordingMicrophoneType | '' {
  return microphoneTypes.find((microphone) => microphone === value) ?? '';
}

export const emptyRecordingDeviceSelection: RecordingDeviceSelection = {
  recordingDeviceCategory: '',
  recordingMicrophoneType: '',
  recordingDeviceDetails: '',
};

export function isRecordingDeviceSelectionValid(
  value: RecordingDeviceSelection,
): value is CompleteRecordingDeviceSelection {
  const hasOther = value.recordingDeviceCategory === 'other' || value.recordingMicrophoneType === 'other_external';
  const detailsLength = Array.from(value.recordingDeviceDetails.trim()).length;
  return Boolean(
    value.recordingDeviceCategory &&
    value.recordingMicrophoneType &&
    (!hasOther || (value.recordingDeviceDetails.length <= 160 && detailsLength >= 2 && detailsLength <= 80)),
  );
}

export function recordingDeviceDetailsRequired(value: RecordingDeviceSelection): boolean {
  return value.recordingDeviceCategory === 'other' || value.recordingMicrophoneType === 'other_external';
}

export function isStructuredRecordingDeviceMetadataValid(value: {
  recordingDeviceCategory?: RecordingDeviceCategory;
  recordingMicrophoneType?: RecordingMicrophoneType;
  recordingDeviceDetails?: string | null;
}): boolean {
  const hasCategory = value.recordingDeviceCategory !== undefined;
  const hasMicrophone = value.recordingMicrophoneType !== undefined;
  if (hasCategory !== hasMicrophone) return false;
  if (!hasCategory) return value.recordingDeviceDetails === undefined || value.recordingDeviceDetails === null;

  const needsDetails = value.recordingDeviceCategory === 'other' || value.recordingMicrophoneType === 'other_external';
  const details = value.recordingDeviceDetails?.trim() ?? '';
  const detailsLength = Array.from(details).length;
  return needsDetails
    ? detailsLength >= 2 && detailsLength <= 80
    : value.recordingDeviceDetails === undefined || value.recordingDeviceDetails === null;
}
